// Post-combat buff picker — shown after winning a red/blue buff camp.
// The player chooses which champion receives the buff.

import type { Scene } from "../engine/scene/SceneManager";
import type { Game } from "../game/Game";
import type { MapNode } from "../map/CampaignMap";
import type { RosterEntry } from "../game/RunState";
import { CHAMPIONS, TIER_COLORS } from "../data/champions";
import { championIconUrl } from "../config/constants";
import { levelForXp } from "../units/UnitStats";
import { MapScene } from "./MapScene";

export class BuffPickScene implements Scene {
  private root: HTMLDivElement;

  constructor(
    private game: Game,
    private node: MapNode,
    private buffType: "redBuff" | "blueBuff",
  ) {
    this.root = document.createElement("div");
    this.root.className = "reward-screen";
  }

  enter(): void {
    const state = this.game.runState;
    if (!state) return;

    const isRed = this.buffType === "redBuff";
    const color = isRed ? "#ff5522" : "#3388ff";

    const title = document.createElement("div");
    title.className = "reward-title";
    title.textContent = isRed ? "RED BUFF" : "BLUE BUFF";
    title.style.color = color;
    this.root.appendChild(title);

    const sub = document.createElement("div");
    sub.style.cssText = "color:var(--text-dim);font-size:11px;letter-spacing:1px;margin-bottom:12px";
    sub.textContent = isRed
      ? "Choose a champion to receive +50% auto-attack damage."
      : "Choose a champion to cast every ability twice.";
    this.root.appendChild(sub);

    const cards = document.createElement("div");
    cards.className = "slot-pick-cards";
    this.root.appendChild(cards);

    for (const entry of state.roster) {
      cards.appendChild(this.buildCard(entry, color));
    }

    this.game.sceneRoot.appendChild(this.root);
  }

  private buildCard(entry: RosterEntry, color: string): HTMLDivElement {
    const def = CHAMPIONS[entry.championId];
    const tierColor = TIER_COLORS[def.tier];
    const level = levelForXp(entry.xp);
    const alreadyHas = entry[this.buffType];

    const card = document.createElement("div");
    card.className = "slot-pick-card";
    card.style.setProperty("--tc", tierColor);
    if (alreadyHas) card.style.opacity = "0.5";

    card.innerHTML = `
      <div class="slot-pick-icon" style="background-image:url('${championIconUrl(def.iconId)}')"></div>
      <div class="slot-pick-info">
        <div class="slot-pick-name" style="color:${tierColor}">${def.displayName}</div>
        <div class="slot-pick-level">Lv ${level}${alreadyHas ? ` · already has <span style="color:${color}">this buff</span>` : ""}</div>
      </div>
    `;
    card.addEventListener("click", () => this.pick(entry));
    return card;
  }

  private pick(entry: RosterEntry): void {
    entry[this.buffType] = true;
    this.game.sceneManager.transition(new MapScene(this.game));
  }

  exit(): void { this.root.remove(); }
  update(_dt: number): void {}
  render(_ctx: CanvasRenderingContext2D): void {}
}
